"use client";

import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import Image from "next/image";
import LanguageSwitcher from "./LanguageSwitcher";
import { useTranslations } from "next-intl";

export const navItems = [
  { label: "Home", id: "hero" , labelAr : "الرئيسية"},
  { label: "About", id: "about", labelAr: "من نحن" },
  { label: "Services", id: "services", labelAr: "الخدمات" },
  { label: "Solutions", id: "solutions", labelAr: "الحلول" },
  { label: "Founder-Led Projects", id: "founder-led-projects", labelAr: "مشاريع بقيادة المؤسس" },
  { label: "Contact", id: "contact", labelAr: "اتصل بنا" },
];

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const t = useTranslations("Nav");

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
      setIsMobileMenuOpen(false);
    }
  };

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        isScrolled
          ? "backdrop-blur-lg border-b border-white/10 shadow-lg"
          : "bg-transparent"
      }`}
      style={{
        backgroundColor: isScrolled ? "rgba(0, 12, 96, 0.85)" : "transparent",
      }}
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
        {/* Logo Section */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="cursor-pointer"
          onClick={() => scrollToSection("hero")}
        >
          <Image
            src={"/dark-logo.png"}
            alt="LogicHub Logo"
            width={120}
            height={40}
            className="object-contain"
          />
        </motion.div>

        {/* Desktop Menu */}
        <div className="flex items-center gap-6">
          <div className="hidden lg:flex items-center gap-8">
            {navItems.map((item, index) => (
              <motion.button
                key={item.id}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 * (index + 1) }}
                onClick={() => scrollToSection(item.id)}
                className="text-sm font-medium text-white/90 transition-colors duration-300 relative group cursor-pointer hover:text-[#d59726]"
              >
                {t(item.label)}
                <span
                  className="absolute -bottom-1 left-0 w-0 h-0.5 transition-all duration-300 group-hover:w-full"
                  style={{ backgroundColor: "#d59726" }}
                ></span>
              </motion.button>
            ))}
          </div>

          {/* Language Switcher & Mobile Toggle */}
          <div className="flex items-center gap-4">
            <LanguageSwitcher isScrolled={isScrolled} />

            <button
              className="lg:hidden cursor-pointer focus:outline-none"
              style={{ color: "#d59726" }}
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          exit={{ opacity: 0, height: 0 }}
          className="lg:hidden border-t border-white/10"
          style={{ backgroundColor: "#000c60" }}
        >
          <div className="flex flex-col gap-2 p-6">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className="text-left text-white/80 hover:text-[#d59726] transition-colors p-3 text-lg font-medium border-b border-white/5"
              >
                {t(item.label)}
              </button>
            ))}
          </div>
        </motion.div>
      )}
    </motion.nav>
  );
}
